import { Link } from 'react-router-dom';

/* Shared shell so the three safety pages read as one family. Content is written
   to match what Souqli actually does today - no claims we can't stand behind. */
function Shell({ eyebrow, title, intro, children }: { eyebrow: string; title: string; intro: string; children: React.ReactNode }) {
  return (
    <div className="bg-cream min-h-[70vh]">
      <section className="section-dark relative text-cream py-16 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F0F12,#1a1208 55%,#0F0F12)' }}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15" />
        <div className="relative max-w-[900px] mx-auto px-6">
          <div className="stamp text-gold text-[10px]">{eyebrow}</div>
          <h1 className="display text-4xl md:text-5xl mt-2">{title}</h1>
          <p className="text-cream/60 text-sm mt-3 max-w-[60ch]">{intro}</p>
        </div>
      </section>
      <div className="max-w-[900px] mx-auto px-6 py-12 space-y-8 text-ink/90 leading-relaxed text-[15px]">
        {children}
        <div className="pt-6 border-t border-warmline text-[13px] text-mute">
          Still need a hand? <Link to="/contact" className="text-gold-dark hover:underline">Contact support</Link> or browse the <Link to="/help" className="text-gold-dark hover:underline">Help Centre</Link>.
        </div>
      </div>
    </div>
  );
}

function Block({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-2">
        <span className="mono text-[11px] text-gold-dark">{n}</span>
        <h2 className="display text-xl text-ink">{title}</h2>
      </div>
      <div className="space-y-2 text-mute">{children}</div>
    </section>
  );
}

export function BuyerProtection() {
  return (
    <Shell eyebrow="BUYER PROTECTION" title="Your money is held safely until you're happy."
      intro="Every Souqli order is payment-protected. Your payment is held by Souqli - never sent straight to the seller - and only released once you've received the item and had time to check it.">
      <Block n="01" title="Payment held until you confirm receipt">
        <p>When you buy, your card is charged and the funds are held by Souqli, not the seller. The seller can see the sale is funded and post with confidence, but the money only moves to them after the item is safely with you.</p>
      </Block>
      <Block n="02" title="Two days to check the item">
        <p>Once delivery is confirmed you have a 48-hour window to inspect what you received. If it's as described, you can confirm straight away; if you do nothing, the order auto-completes after two days and the seller is paid. Raise a Buyer Protection claim within 2 days of delivery, or within 7 days of the expected date if an item never arrives.</p>
      </Block>
      <Block n="03" title="What's covered">
        <p>Raise an issue within the window if your item doesn't arrive, arrives damaged, is not as described, or is counterfeit. While an issue is open, the funds stay held - they are not released to the seller.</p>
      </Block>
      <Block n="04" title="Refunds from the held balance">
        <p>Because the money is still held, approved refunds are returned to you quickly from the held balance rather than waiting on the seller. Refunds go back to your original payment method; once we initiate one, banks typically take 7–10 business days. See <Link to="/disputes" className="text-gold-dark hover:underline">Dispute resolution</Link> for how a claim is decided.</p>
      </Block>
      <Block n="05" title="Fees & the full policy">
        <p>Buyer protection costs 4.5% + £0.70 per order as a guest, or 3.5% + £0.60 once a member passes their monthly included allowance (12 orders / £300). If an eligible order is fully refunded, that fee is refunded too. The full terms — claim windows, exclusions, returns and return postage — are in our <Link to="/refund-policy" className="text-gold-dark hover:underline">Refund &amp; Buyer Protection Policy</Link>.</p>
      </Block>
    </Shell>
  );
}

export function DisputeResolution() {
  return (
    <Shell eyebrow="DISPUTE RESOLUTION" title="If something's wrong, we sort it out fairly."
      intro="Most orders go smoothly, but if one doesn't, here's exactly how a dispute works - and why your money stays protected the whole way through.">
      <Block n="01" title="Open it from your order">
        <p>Go to the order in your dashboard and start a return or report a problem within the window shown there. Tell us what's wrong and add photos - evidence makes a claim faster to resolve.</p>
      </Block>
      <Block n="02" title="Return before refund (where it applies)">
        <p>For "not as described" or "damaged" claims, the item is sent back to the seller with tracked postage. Once the return is confirmed in transit or received, your refund is issued. Return postage is normally paid by the buyer, but where the final decision finds the seller at fault — a wrong item, not as described, damage from the seller's packaging, or a counterfeit — the seller bears the reasonable cost of return.</p>
      </Block>
      <Block n="03" title="If it isn't resolved between you">
        <p>If you and the seller can't agree, Souqli reviews the evidence - messages, photos, tracking scans and parcel weights - and makes a decision. The funds remain on hold until that decision is made.</p>
      </Block>
      <Block n="04" title="Outcomes">
        <p>A dispute ends in one of: a full or partial refund to you, release of funds to the seller, or a tracked return followed by a refund. You'll be notified at each step. Approved refunds return to your original payment method, normally within 7–10 business days of being initiated. Full details are in our <Link to="/refund-policy" className="text-gold-dark hover:underline">Refund &amp; Buyer Protection Policy</Link>.</p>
      </Block>
    </Shell>
  );
}

export function StolenGoods() {
  return (
    <Shell eyebrow="STOLEN GOODS CHECK" title="Keeping stolen electronics off Souqli."
      intro="Electronics carry extra checks before they can be listed, so buyers can shop phones and gadgets with more confidence.">
      <Block n="01" title="IMEI / serial required">
        <p>Listing a phone, laptop or other serialised device requires its IMEI or serial number plus photos. No serial, no listing for those categories.</p>
      </Block>
      <Block n="02" title="Duplicate detection">
        <p>If the same IMEI or serial is listed by more than one account, it's automatically flagged and blocked - a common signature of a device that shouldn't be resold.</p>
      </Block>
      <Block n="03" title="Stolen-property screening">
        <p>Where screening is enabled, serials are checked against stolen-property data (via CheckMEND, which draws on police and insurer loss registers) and anything flagged is removed before it reaches buyers.</p>
      </Block>
      <Block n="04" title="Think an item is stolen?">
        <p>If you suspect a listing - or something you bought - is stolen, <Link to="/contact" className="text-gold-dark hover:underline">tell us</Link> right away. Buyers are covered by <Link to="/buyer-protection" className="text-gold-dark hover:underline">Buyer Protection</Link> while we investigate.</p>
      </Block>
    </Shell>
  );
}
